import { cn } from "@/lib/utils";
import { InputStyles } from "@/utils/inputStyles";
import { Placeholder } from "@tiptap/extension-placeholder";

import { computePosition, offset } from "@floating-ui/dom";
import Document from "@tiptap/extension-document";
import History from "@tiptap/extension-history";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { EditorContent, ReactRenderer, useEditor } from "@tiptap/react";
import { observer } from "mobx-react-lite";
import React, { useCallback, useMemo } from "react";
import { useIsOutputMessage } from "../Playground2/Sidebar/OutputsWidget/outputs-widget-context";
import ContextMenu, { ContextMenuHandle } from "./ContextMenu";
import { Commands, getSuggestionItems } from "./ContextMenu/utils";
import MarkdownStyleDecoration from "./Decorations/MarkdownStyleDecorations";
import { MarkdownFormatting } from "./Extensions/MarkdownFormatting";
import {
  useDetectedInputVariableSuggestions,
  useEditorMode,
  useExternalChanges,
} from "./hooks";
import "./markdown-styles.css";
import DatasetExamplesNode from "./Nodes/DatasetExamples/DatasetExamplesNode";
import { VariableMention } from "./Nodes/InputVariable/VariableMention";
import SnippetNode from "./Nodes/Snippet/SnippetNode";
import {
  escapeXml,
  regexToHTML,
  serializeToMarkdown,
  unescapeXml,
} from "./utils";

interface TiptapEditorProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  onCreate?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  containerClassName?: string;
  markdownEnabled?: boolean;
}

const TiptapEditor: React.FC<TiptapEditorProps> = ({
  onChange,
  onCreate,
  placeholder: customPlaceholder,
  className,
  readOnly,
  value,
  containerClassName = "",
  markdownEnabled = true,
}) => {
  const contentClassName = useMemo(
    () =>
      cn(
        InputStyles.Base,
        InputStyles.Textarea,
        "relative z-10 w-full overflow-auto flex-col",
        readOnly ? "cursor-default" : "",
        className,
      ),
    [readOnly, className],
  );

  const inputVariableSuggestions = useDetectedInputVariableSuggestions();
  const mode = useEditorMode();

  const initialContent = useMemo(
    () => (value ? regexToHTML(escapeXml(String(value)), mode) : ""),

    [mode, value],
  );

  const handleUpdate = useCallback(
    ({ editor }: { editor: any }) => {
      if (onChange) {
        const serialized = unescapeXml(serializeToMarkdown(editor));
        onChange({
          target: { value: serialized },
        } as React.ChangeEvent<HTMLTextAreaElement>);
      }
    },
    [onChange],
  );

  const handleCreate = useCallback(
    ({ editor }: { editor: any }) => {
      if (onCreate) {
        const serialized = unescapeXml(serializeToMarkdown(editor));
        onCreate({
          target: { value: serialized },
        } as React.ChangeEvent<HTMLTextAreaElement>);
      }
    },
    [onCreate],
  );

  const isOutput = useIsOutputMessage();

  const extensions = useMemo(() => {
    const baseExtensions = [
      SnippetNode,
      MarkdownStyleDecoration.configure({ mode, isOutput, markdownEnabled }),
      DatasetExamplesNode,
      VariableMention.configure({
        mode,
        suggestion: {
          ...inputVariableSuggestions,
          char: "{",
        },
      }),
      Document,
      Paragraph,
      Text,
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (customPlaceholder) {
            return customPlaceholder;
          }
          if (node.type.name === "heading") {
            return "What's the title?";
          }
          return 'Type "/" for commands...';
        },
        showOnlyWhenEditable: true,
        includeChildren: true,
      }),
      Commands.configure({
        suggestion: {
          char: "/",
          items: ({ query }: { query: string }) => {
            return getSuggestionItems(query);
          },
          render: () => {
            let component: ReactRenderer<ContextMenuHandle>;
            let popup: any;

            return {
              onStart: (props: any) => {
                component = new ReactRenderer(ContextMenu, {
                  props,
                  editor: props.editor,
                });

                if (!props.clientRect) {
                  return;
                }

                const container = document.createElement("div");
                container.style.zIndex = "9999";
                document.body.appendChild(container);
                container.appendChild(component.element);

                computePosition(
                  {
                    getBoundingClientRect: props.clientRect,
                    contextElement: props.editor.options.element,
                  },
                  container,
                  {
                    placement: "bottom-start",
                    middleware: [offset(6)],
                  },
                ).then(({ x, y }) => {
                  container.style.position = "absolute";
                  container.style.left = `${x}px`;
                  container.style.top = `${y}px`;
                  container.style.zIndex = "9999";
                });

                popup = { container };
              },

              onUpdate(props: any) {
                component.updateProps(props);

                if (!props.clientRect || !popup.container) {
                  return;
                }

                computePosition(
                  {
                    getBoundingClientRect: props.clientRect,
                    contextElement: props.editor.options.element,
                  },
                  popup.container,
                  {
                    placement: "bottom-start",
                    middleware: [offset(6)],
                  },
                ).then(({ x, y }) => {
                  popup.container.style.left = `${x}px`;
                  popup.container.style.top = `${y}px`;
                });
              },

              onKeyDown(props: any) {
                if (props.event.key === "Escape") {
                  if (popup.container) {
                    popup.container.style.display = "none";
                  }
                  return true;
                }

                return component.ref?.onKeyDown(props.event) || false;
              },

              onExit() {
                if (popup.container) {
                  document.body.removeChild(popup.container);
                }
                component.destroy();
              },
            };
          },
        },
      }),
      History,
    ];

    if (markdownEnabled) {
      baseExtensions.push(MarkdownFormatting);
    }

    return baseExtensions;
  }, [
    markdownEnabled,
    mode,
    isOutput,
    inputVariableSuggestions,
    customPlaceholder,
  ]);

  const editor = useEditor({
    extensions,
    parseOptions: {
      preserveWhitespace: "full",
    },
    editorProps: {
      editable: () => !readOnly,
      attributes: {
        class: `${contentClassName}`,
      },
      handleKeyDown: (view, event) => {
        if (event.key === "Tab") {
          event.preventDefault();
          view.dispatch(view.state.tr.insertText("\t"));
          return true;
        }
        return false;
      },
    },
    content: initialContent,
    onCreate: handleCreate,
    onUpdate: handleUpdate,
  });

  useExternalChanges(editor, initialContent);

  return (
    <EditorContent
      editor={editor}
      className={`content-editor outline-none ${containerClassName}`}
    />
  );
};

interface ContentAreaProps extends TiptapEditorProps {
  markdownEnabled?: boolean;
}

const ContentArea: React.FC<ContentAreaProps> = observer(
  ({ markdownEnabled = true, ...props }) => {
    const mode = useEditorMode();

    return (
      <TiptapEditor
        {...props}
        markdownEnabled={markdownEnabled}
        key={`${mode}-${markdownEnabled}`}
      />
    );
  },
);

export default ContentArea;
